/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import {ReservationJournal} from 'types/entities/ReservationJournal'
import Dates from 'utils/Dates'
import UiButton, {UiButtonStyle} from 'components/Ui/UiButton'
import {useAppDispatch} from 'helpers/store'
import {closeSecondaryModal} from 'helpers/actions'
import {DATE_FORMATS} from "constants/DateFormats";

interface BookingSellerJournalAllModalProps {
    journals: ReservationJournal[]
}

const BookingSellerJournalAllModal: React.FC<
    BookingSellerJournalAllModalProps
> = ({ journals }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const sortedJournals = [...journals].sort(
        (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )

    return (
        <div className="BookingSellerJournalAllModal flex flex-col gap-4 p-4 max-h-[70vh]">
            <div className="flex-1 overflow-y-auto flex flex-col gap-4 pr-1">
                {sortedJournals.length > 0 ? (
                    sortedJournals.map((journal) => (
                        <div
                            key={journal.id}
                            className="flex flex-col border-b border-gray-100 dark:border-gray-800 last:border-0 pb-3"
                        >
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-tight">
                                    {Dates.dayjs(journal.created_at).format(
										DATE_FORMATS.DATE_TIME_SEC
									)}
                                </span>
                                {journal.driver && (
                                    <span className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-500">
                                        {journal.driver.name}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                                {journal.text}
                            </p>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-8 text-gray-400 italic">
                        {t('No journal entries found')}
                    </div>
                )}
            </div>
            <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-800">
                <UiButton
                    title={t('Close')}
                    style={UiButtonStyle.Secondary}
                    onClick={() => dispatch(closeSecondaryModal())}
                />
            </div>
        </div>
    )
}

export default BookingSellerJournalAllModal
